import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useMutation, type UseMutationOptions} from '@github-ui/react-query'
import {
  parseJSONWithBetterErrors,
  StaleContentError,
  throwErrorsIfBadResponse,
  throwIfStaleResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {usePullRequestBasePath} from '../stores/layout-selectors'
import {getCurrentUser} from '../components/comments/utils/get-author-filters'
import {reactFetch} from '@github-ui/verified-fetch'
import {setReviewSummaryQueryData} from '../page-data/loaders/use-conversation-comments'
import type {
  UpdateIssueCommentResponse,
  UpdateIssueCommentStaleResponse,
  UpdateIssueCommentVariables,
} from './use-update-issue-comment-mutation'

// types are kept in sync with issue comment mutation for ease of code reuse
type UpdateReviewSummaryVariables = UpdateIssueCommentVariables
type UpdateReviewSummaryResponse = UpdateIssueCommentResponse

type InternalUpdateReviewSummaryResponse =
  | UpdateReviewSummaryResponse
  | UpdateIssueCommentStaleResponse
  | {error?: string}

/**
 * Mutation to edit a review summary, automatically updating the corresponding item in the `conversation_comments`
 * query store.
 */
export function useUpdateReviewSummaryMutation(
  options: Omit<UseMutationOptions<unknown, Error, UpdateReviewSummaryVariables, unknown>, 'mutationFn'> = {},
) {
  const basePath = usePullRequestBasePath()

  return useMutation({
    ...options,
    mutationFn: async ({id, newBody, bodyVersion}: UpdateReviewSummaryVariables) => {
      if (!isFeatureEnabled('prs_new_conversation_comments_api'))
        throw new Error(
          'useUpdateReviewSummaryMutation requires `prs_new_conversation_comments_api` feature flag to be enabled',
        )

      // Comment editing works repo-wide across PRs and issues with unique comment IDs; it's not scoped to just this PR
      const path = `${basePath}/reviews/${id}/update`

      const body = new FormData()
      body.set('pull_request_review[body]', newBody)
      body.set('body_version', bodyVersion)

      const response = await reactFetch(path, {
        method: 'PUT',
        body,
        headers: {
          Accept: 'application/json',
        },
      })

      const json: InternalUpdateReviewSummaryResponse = await parseJSONWithBetterErrors(response)

      throwIfStaleResponse(response, json)
      throwErrorsIfBadResponse(response, json)

      return json as UpdateReviewSummaryResponse
    },
    onError: async (error, variables, onMutateResult, context) => {
      if (error instanceof StaleContentError) {
        const freshData = (error as StaleContentError<UpdateIssueCommentStaleResponse>).freshData
        setReviewSummaryQueryData(context.client, basePath, variables.id, comment => {
          comment.body = freshData.updated_markdown
          comment.bodyHTML = freshData.updated_html
          comment.bodyVersion = freshData.version
          // ideally we'd set lastUserContentEdit too, but we don't have that data
        })
      }

      return options.onError?.(error, variables, onMutateResult, context)
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      const editor = getCurrentUser() ?? null

      setReviewSummaryQueryData(context.client, basePath, variables.id, comment => {
        comment.body = response.source
        comment.bodyHTML = response.body
        comment.bodyVersion = response.newBodyVersion
        comment.lastUserContentEdit = {
          editor,
          id: null,
        }
      })

      return options.onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
